/**
 * name: @oplayer/hls
 * version: v1.2.8
 * description: Hls plugin for oplayer
 * author: shiyiya
 * homepage: https://github.com/shiyiya/oplayer
 */
const PLUGIN_NAME = "oplayer-plugin-hls";
let imported = globalThis.Hls;
const defaultMatcher = (video, source) => !(Boolean(video.canPlayType("application/x-mpegURL")) || Boolean(video.canPlayType("application/vnd.apple.mpegURL"))) && (source.format === "hls" || source.format === "m3u8" || (source.format === "auto" || typeof source.format === "undefined") && /m3u8(#|\?|$)/i.test(source.src));
const generateSetting = function(player, instance) {
  let options = arguments.length > 2 && arguments[2] !== void 0 ? arguments[2] : {};
  if (options.qualityControl) {
    instance.once(imported.Events.MANIFEST_PARSED, () => {
      settingUpdater({
        icon: player.plugins.ui.icons.quality,
        name: "Quality",
        settings() {
          if (instance.levels.length > 1) {
            return instance.levels.reduce((pre, level, i) => {
              let name = (level.name || level.height).toString();
              if (isFinite(+name))
                name += "p";
              if (options.withBitrate) {
                const kb = level.bitrate / 1e3;
                const useMb = kb > 1e3;
                const number = useMb ? ~~(kb / 1e3) : Math.floor(kb);
                name += " (" + number + (useMb ? "m" : "k") + "bps)";
              }
              pre.push({
                name,
                default: instance.currentLevel == i,
                value: i
              });
              return pre;
            }, [{
              name: player.locales.get("Auto"),
              default: instance.autoLevelEnabled,
              value: -1
            }]);
          }
          return [];
        },
        onChange(it) {
          if (options.qualitySwitch == "immediate") {
            instance.currentLevel = it.value;
            if (it.value !== -1)
              instance.loadLevel = it.value;
          } else {
            instance.nextLevel = it.value;
            if (it.value !== -1)
              instance.nextLoadLevel = it.value;
          }
        }
      });
    });
    instance.on(imported.Events.LEVEL_SWITCHED, function menuUpdater(_, _ref) {
      let level = _ref.level;
      if (instance.autoLevelEnabled) {
        const height = instance.levels[level].height;
        const levelName = player.locales.get("Auto") + (height ? " (" + height + "p)" : "");
        player.plugins.ui.setting.updateLabel(PLUGIN_NAME + "-Quality", levelName);
      } else {
        player.plugins.ui.setting.select(PLUGIN_NAME + "-Quality", level + 1, false);
      }
    });
  }
  if (options.audioControl)
    instance.on(imported.Events.AUDIO_TRACK_LOADED, () => {
      settingUpdater({
        icon: player.plugins.ui.icons.lang,
        name: "Language",
        settings() {
          if (instance.audioTracks.length > 1) {
            return instance.audioTracks.map((_ref2) => {
              let name = _ref2.name, lang = _ref2.lang, id = _ref2.id;
              return {
                name: lang || name,
                default: instance.audioTrack == id,
                value: id
              };
            });
          }
          return [];
        },
        onChange(it) {
          instance.audioTrack = it.value;
        }
      });
    });
  if (options.textControl)
    instance.on(imported.Events.SUBTITLE_TRACK_LOADED, () => {
      settingUpdater({
        icon: player.plugins.ui.icons.subtitle,
        name: "Subtitle",
        settings() {
          if (instance.subtitleTracks.length > 1) {
            return instance.subtitleTracks.reduce((pre, _ref3) => {
              let name = _ref3.name, lang = _ref3.lang, id = _ref3.id;
              return pre.push({
                name: lang || name,
                default: instance.subtitleTrack == id,
                value: id
              }), pre;
            }, [{
              name: player.locales.get("Off"),
              default: !instance.subtitleDisplay,
              value: -1
            }]);
          }
          return [];
        },
        onChange(_ref4) {
          let value = _ref4.value;
          if (instance.subtitleDisplay = !(value == -1)) {
            instance.subtitleTrack = value;
          }
        }
      });
    });
  function settingUpdater(arg) {
    const settings = arg.settings();
    if (settings.length < 2)
      return;
    const name = arg.name, icon = arg.icon, onChange = arg.onChange;
    player.plugins.ui.setting.unregister(PLUGIN_NAME + "-" + name);
    player.plugins.ui.setting.register({
      name: player.locales.get(name),
      icon,
      onChange,
      type: "selector",
      key: PLUGIN_NAME + "-" + name,
      children: settings
    });
  }
};
const removeSetting = (player) => {
  ["Quality", "Language", "Subtitle"].forEach((it) => {
    var _a;
    return (_a = player.plugins.ui) == null ? void 0 : _a.setting.unregister(PLUGIN_NAME + "-" + it);
  });
};
const plugin = function() {
  let _ref5 = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : {}, config = _ref5.config, _ref5$showWarning = _ref5.showWarning, showWarning = _ref5$showWarning === void 0 ? false : _ref5$showWarning, _ref5$withBitrate = _ref5.withBitrate, withBitrate = _ref5$withBitrate === void 0 ? false : _ref5$withBitrate, _ref5$qualityControl = _ref5.qualityControl, qualityControl = _ref5$qualityControl === void 0 ? true : _ref5$qualityControl, _ref5$qualitySwitch = _ref5.qualitySwitch, qualitySwitch = _ref5$qualitySwitch === void 0 ? "immediate" : _ref5$qualitySwitch, _ref5$audioControl = _ref5.audioControl, audioControl = _ref5$audioControl === void 0 ? true : _ref5$audioControl, _ref5$textControl = _ref5.textControl, textControl = _ref5$textControl === void 0 ? true : _ref5$textControl, _ref5$matcher = _ref5.matcher, matcher = _ref5$matcher === void 0 ? defaultMatcher : _ref5$matcher;
  let instance;
  let instanceDestroy;
  function tryDestroy(player) {
    if (instance) {
      removeSetting(player);
      instanceDestroy == null ? void 0 : instanceDestroy.call(instance);
      instanceDestroy = null;
      instance = null;
    }
  }
  return {
    key: "hls",
    name: PLUGIN_NAME,
    //@ts-ignore
    version: "1.2.8",
    load: async (player, source) => {
      var _a;
      if (!matcher(player.$video, source))
        return false;
      imported ?? (imported = (await import("hls.js/dist/hls.min.js")).default);
      if (!imported.isSupported())
        return false;
      instance = new imported(config);
      instance.loadSource(source.src);
      instance.attachMedia(player.$video);
      if (!player.isNativeUI && ((_a = player.plugins.ui) == null ? void 0 : _a.setting)) {
        generateSetting(player, instance, {
          qualityControl,
          qualitySwitch,
          withBitrate,
          audioControl,
          textControl
        });
      }
      instance.on(imported.Events.ERROR, function(_, data) {
        const type = data.type, details = data.details, fatal = data.fatal;
        if (fatal) {
          player.hasError = true;
          player.emit("error", Object.assign({}, data, {
            pluginName: PLUGIN_NAME,
            message: type + ": " + details
          }));
        } else {
          if (showWarning) {
            player.emit("notice", Object.assign({}, data, {
              pluginName: PLUGIN_NAME,
              text: type + ": " + details
            }));
          }
        }
      });
      instanceDestroy = instance.destroy;
      instance.destroy = () => {
        tryDestroy(player);
      };
      return instance;
    },
    apply: (player) => {
      player.on("destroy", () => {
        tryDestroy(player);
      });
      return () => imported;
    }
  };
};
export {
  plugin as default
};
